import {
  CustomPaging,
  GridColumnExtension,
  IntegratedPaging,
  PagingState,
  Table as TableBase,
} from "@devexpress/dx-react-grid";
import {
  ColumnChooser,
  Grid,
  PagingPanel,
  Table,
  TableColumnVisibility,
  TableFixedColumns,
  TableHeaderRow,
  Toolbar,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import arrayMove from "array-move";
import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Accordion, Button, Card } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { FormattedMessage, useIntl } from "react-intl";
import { SizeMe } from "react-sizeme";
import SweetAlert from "sweetalert2";
import { useLocaleProvider } from "../../../../components/locale-provider";
import { SectionHeading, SectionSubHeading } from "../../../../components/section-heading";
import { IAuthzAction } from "../../../../lib/action-authorizer";
import filterPills, { FilterValues } from "../../../../lib/filterPills";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { TableActionProps, TableRowActionProps } from "../../actions";
import { Identifiable } from "../../context";
import { useLoaderState } from "../../hooks";
import { Renderer } from "../../renderers";
import { FilterWidgetProps } from "../../widgets";
import {
  Chevron,
  ColumnsMessage,
  CrudListContainer,
  CustomCardTitle,
  defaultListMessages,
  ExportButton,
  ListMessages,
  Loader,
  LoadError,
  NoData,
  StyledCard,
  StyledCardDeck,
  StyledReactTooltip,
  StyledSimpleRow,
  StyledTableBody,
  StyledTableRow,
  TableColumns,
  TableFilter,
  TableFilters,
  TitleChevronContainer,
} from "./index";

type ColumnConfig<T> = {
  renderer?: Renderer<T>;
  width?: number;
  button?: (props: any) => React.ReactNode;
  align?: "left" | "center" | "right" | undefined;
};

type TableColumnConfig<T> = ColumnConfig<T> & {
  children?: {
    [key: string]: ColumnConfig<T> & {
      renderer: Renderer<T>;
    };
  };
};

function isTableConfig<T>(
  config?: Renderer<T> | TableColumnConfig<T>
): config is TableColumnConfig<T> {
  const tableColumnConfig: TableColumnConfig<T> = config as TableColumnConfig<T>;
  return !!(tableColumnConfig.renderer || tableColumnConfig.children);
}

export type OnGetAllParams<F, IsPaging extends boolean = false> = IsPaging extends true
  ? {
      filterValues: Partial<F> | FilterValues;
      currentPage: number;
      pageSize: number;
      setTotalRowsCount: React.Dispatch<React.SetStateAction<number>>;
    }
  : {
      filterValues?: Partial<F> | FilterValues;
      currentPage?: number;
      pageSize?: number;
      setTotalRowsCount?: React.Dispatch<React.SetStateAction<number>>;
    };

export interface CrudListProps<T extends Identifiable, F> {
  showSuccessMessage?: boolean;
  paging?: boolean;
  uncontrolledPaging?: boolean;
  columnVisibility?: boolean;
  columnVisibilityExtensions?: {
    columnName: string;
    togglingEnabled: boolean;
  }[];
  pageSizes?: number[];
  initialHiddenColumnNames?: string[];
  totalRowsCount?: number;
  currentPage?: number;
  defaultPageSize?: number;
  currentPageSize?: number;
  managerName?: string;
  customRow?: (props: T[] | undefined) => React.ComponentType<TableBase.DataRowProps>;
  setCurrentPageSize?: React.Dispatch<React.SetStateAction<number>>;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  actions?: (props: TableActionProps<T>) => React.ReactNode;
  actionsWidth?: number;
  rowActionsTooltip?: () => React.ReactNode;
  rowActions?: (props: TableRowActionProps<T>) => React.ReactNode;
  additionalHeaderContent?: any;
  additionalFooterContent?: any;
  CSVRenderers?: { [key: string]: (value: any, fields: any) => string };
  listHeight?: number | string;
  hideExportButton?: boolean;

  onGetAll?(props: OnGetAllParams<F>): Promise<T[] | undefined>;

  onReorder?(id: T["id"], afterId: T["id"] | undefined): Promise<void>;

  onBatchCancellation?: any;
  showBatchCancellation?: boolean;

  onExport?(props: {
    filterValues?: Partial<F>;
    currentPage?: number;
    pageSize?: number;
  }): Promise<void> | void;

  onExportText?: string;
  columns: TableColumns<T>;
  messages?: ListMessages;
  subHeader?: string;
  filters?: TableFilters<T, F>;
  forceLoad?: any;
  fixedActions?: boolean;
  nestedData?: T[];
  canBulkUpdate?: IAuthzAction;
  onBulkUpdate?: () => void;
  filtersClosedByDefault?: boolean;
  firstLoad?: boolean;
}

const getRowId = (row: any) => {
  return row.id;
};

/**
 * A generic CRUD list component with filtering, sorting, pagination, exporting and reordering capabilities.

 * @deprecated Use CrudListUncontrolled instead. Uncontrolled version provides better performance
 * and simpler state management. Can be used with useQuery hook for data fetching.
 */
export function CrudList<T extends Identifiable, F = {}>({
  actions,
  actionsWidth = 290,
  rowActionsTooltip,
  rowActions,
  columns,
  onGetAll,
  onExport,
  onExportText,
  onReorder,
  showSuccessMessage,
  messages = defaultListMessages,
  subHeader,
  filters,
  paging,
  uncontrolledPaging,
  columnVisibility,
  columnVisibilityExtensions = [],
  initialHiddenColumnNames,
  pageSizes,
  defaultPageSize,
  managerName,
  customRow,
  additionalHeaderContent,
  additionalFooterContent,
  CSVRenderers,
  listHeight,
  forceLoad,
  onBatchCancellation,
  showBatchCancellation,
  fixedActions,
  nestedData,
  canBulkUpdate,
  onBulkUpdate,
  hideExportButton,
  filtersClosedByDefault = true,
  firstLoad = true,
}: CrudListProps<T, F>) {
  const intl = useIntl();
  const locale = useLocaleProvider();
  const [{ loading, loadError, data }, loaderDispatch] = useLoaderState<T[]>({
    loading: firstLoad,
  });
  const [{ loading: exportLoading }, exportLoaderDispatch] = useLoaderState<T[]>({
    loading: false,
  });
  const [reordering, setReordering] = useState(false);
  const [syncFilterValues, setSyncFilterValues] = useState<Partial<F>>({});
  const [asyncFilterValues, setAsyncFilterValues] = useState<Partial<F>>(() =>
    window.localStorage.getItem("asyncFilterValues") && managerName
      ? JSON.parse(window.localStorage.getItem("asyncFilterValues") as string)[managerName]
      : {}
  );
  const [tempAsyncFilterValues, setTempAsyncFilterValues] = useState<Partial<F>>(
    asyncFilterValues || {}
  );
  const [datePickerInFocus, setDatePickerInFocus] = useState();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [toggleClosed, setToggleClosed] = useState(filtersClosedByDefault);
  const [hiddenColumnNames, setHiddenColumnNames] = useState(() =>
    window.localStorage.getItem("hiddenColumnNames")
      ? JSON.parse(window.localStorage.getItem("hiddenColumnNames") as string)
      : initialHiddenColumnNames
      ? initialHiddenColumnNames
      : []
  );
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  useEffect(() => {
    if (hiddenColumnNames) {
      window.localStorage.setItem("hiddenColumnNames", JSON.stringify(hiddenColumnNames));
      // load();
    }
    if (asyncFilterValues && managerName) {
      window.localStorage.setItem(
        "asyncFilterValues",
        JSON.stringify({
          ...(JSON.parse(window.localStorage.getItem("asyncFilterValues") as string) || {}),
          [managerName]: asyncFilterValues,
        })
      );
    }
  }, [hiddenColumnNames, asyncFilterValues, managerName]);

  const columnExtensions = useMemo(
    () =>
      Object.keys(columns)
        .reduce((r, k) => {
          const config = columns[k as keyof T];
          if (isTableConfig(config) && config.children) {
            const children = Object.keys(config.children).map(column => {
              const width = (config.children && config.children[column].width) || undefined;
              const align = (config.children && config.children[column].align) || undefined;
              return {
                columnName: `${k}.${column}`,
                width,
                align,
              };
            });
            r = [...r, ...children];
          } else {
            const width = isTableConfig(config) ? config.width : undefined;
            const align = isTableConfig(config) ? config.align : undefined;
            r.push({
              columnName: k,
              width,
              align,
            });
          }
          return r;
        }, [] as Array<GridColumnExtension>)
        .concat({ columnName: "__actions", width: actionsWidth, align: "left" }),
    [actionsWidth, columns]
  );

  const applySyncFilters = useCallback(
    (data: T[]) => {
      if (!filters) return data;
      const filtersToApply = Object.keys(filters)
        .map(k => {
          const key = k as keyof T & keyof F;
          const filter = filters[key] as TableFilter<any, any> | undefined;
          return { key, filter };
        })
        .filter(
          ({ key, filter }) =>
            filter !== undefined &&
            filter.filterFn !== "async" &&
            syncFilterValues[key] !== undefined
        );

      return data.filter(value =>
        filtersToApply.reduce<boolean>((prev, next) => {
          const filterFn = next.filter?.filterFn;
          return (
            prev &&
            typeof filterFn === "function" &&
            filterFn(value[next.key], syncFilterValues[next.key], intl)
          );
        }, true)
      );
    },
    [syncFilterValues, filters, intl]
  );

  const getFilterValues = useCallback(
    (filterValues?: Partial<F>) => {
      if (!filters) return;
      return filterValues ?? asyncFilterValues;
    },
    [filters, asyncFilterValues]
  );

  const load = useCallback(
    async (props?: { page?: number; pageSize?: number; filters?: Partial<F> }) => {
      loaderDispatch({ type: "load_start" });
      try {
        const finalPage = props?.page ?? currentPage;
        const finalPageSize = props?.pageSize ?? pageSize;
        let filterValues = getFilterValues(props?.filters);
        const data = onGetAll
          ? await onGetAll({
              filterValues,
              currentPage: finalPage,
              pageSize: finalPageSize,
              setTotalRowsCount,
            })
          : nestedData;
        if (data) {
          loaderDispatch({ type: "load_success", data: data });
        }
      } catch (e) {
        console.error(e);
        loaderDispatch({
          type: "load_error",
          errorMessageId: "serviceError",
        });
      }
    },
    // forceLoad is used to reload the list from parent component
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onGetAll,
      loaderDispatch,
      filters,
      asyncFilterValues,
      currentPage,
      pageSize,
      setTotalRowsCount,
      forceLoad,
    ]
  );

  useEffect(() => {
    if (!firstLoad) return;
    load();
  }, [load, firstLoad]);

  useEffect(() => {
    if (typeof forceLoad === "undefined") return;
    load();
  }, [load, forceLoad]);

  const reload = useCallback(() => {
    load();
  }, [load]);

  const NoDataCell = useMemo(() => {
    return (props: Table.NoDataCellProps) =>
      loading ? (
        <Loader loadMessage={messages.loading} />
      ) : loadError ? (
        <LoadError
          retry={reload}
          failMessage={messages.loadError}
          retryMessage={messages.loadRetry}
        />
      ) : (
        <NoData noDataMessage={messages.noData} />
      );
  }, [loading, loadError, messages, reload]);

  const CellRenderer = useMemo(
    () => (props: any) => {
      const columnWidth =
        props.tableColumn.width ||
        document.getElementById(`column-id-${props.column.name}`)?.offsetWidth;

      const columnAlign = props.tableColumn.align;

      const rowType =
        Array.isArray(data) && data.findIndex(it => it.id === props.row.id) % 2 === 0
          ? "odd"
          : "even";

      if (props.column.name === "__actions") {
        return (
          <>
            {rowActions &&
              rowActions({
                rows: data,
                row: data ? data.find(row => row.id === props.row.id) : props.row,
                onReloadList: load,
                rest: {
                  ...props,
                  className: `${props.className} ${rowType}`,
                },
              })}
          </>
        );
      }
      const [field, childField] = props.column.name.split(".");
      const config = columns[field as keyof T] as Renderer<unknown> | TableColumnConfig<unknown>;
      const button = (config as ColumnConfig<unknown>).button;
      let R = config && isTableConfig(config) ? config.renderer : config;
      if (isTableConfig(config) && childField && config.children) {
        R = config.children[childField].renderer;
      }
      if (!R) {
        return <td />;
      }
      return (
        <td style={{ width: columnWidth, textAlign: columnAlign }}>
          <R value={props.value} button={button} rows={props.row} />
        </td>
      );
    },
    [rowActions, columns, load, data]
  );

  const tableColumns = useMemo(() => {
    const objectColumns = Object.keys(columns).reduce((results, field) => {
      const column = columns[field as keyof T];
      if (isTableConfig(column) && column.children) {
        const children = Object.keys(column.children).map(childField => ({
          name: `${field}.${childField}`,
          title: messages?.tableHeaders?.[childField]
            ? intl.formatMessage(messages?.tableHeaders[childField])
            : childField,
        }));
        results = [...results, ...children];
      } else {
        results.push({
          name: field,
          title: messages?.tableHeaders?.[field]
            ? intl.formatMessage(messages?.tableHeaders[field])
            : field,
        });
      }
      return results;
    }, [] as Array<any>);

    if (rowActions) {
      objectColumns.push({
        name: "__actions",
        title: intl.formatMessage(messages?.tableActions),
      });
    }
    return objectColumns;
  }, [columns, intl, rowActions, messages]);

  const rowData = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.map(d => {
      const dataByColumns = tableColumns.reduce(
        (r, { name: columnName }) => {
          const [field] = columnName.split(".");
          return {
            ...r,
            [columnName]: d[field as keyof T],
          };
        },
        { ...d }
      );
      return dataByColumns;
    });
  }, [data, tableColumns]);

  const filteredData = useMemo(
    () => (rowData === undefined ? undefined : applySyncFilters(rowData)),
    [applySyncFilters, rowData]
  );
  const rows = useMemo(() => filteredData || [], [filteredData]);

  const handleReorder = useCallback(
    async ({ oldIndex, newIndex }) => {
      //Don't call while a call is in progress;
      if (reordering) return;

      const oldRows = rows;
      loaderDispatch({
        type: "load_success",
        data: arrayMove(rows, oldIndex, newIndex),
      });
      if (onReorder) {
        setReordering(true);
        if (oldIndex <= newIndex) {
          newIndex = newIndex + 1;
        }
        const entityId = rows[oldIndex].id;
        const afterId = newIndex > 0 ? rows[newIndex - 1].id : undefined;
        try {
          await onReorder(entityId, afterId);
        } catch (e) {
          console.log("Error while reordering", e);
          loaderDispatch({ type: "load_success", data: oldRows });
          await SweetAlert.fire({
            icon: "error",
            title: intl.formatMessage(messages?.reorderFailedTitle),
            text: intl.formatMessage(messages?.reorderFailedContent),
          });
        } finally {
          setReordering(false);
        }
      }
    },
    [rows, onReorder, setReordering, reordering, loaderDispatch, intl, messages]
  );

  const Body = useMemo(
    () => ({ row, ...restProps }: any) => {
      return <StyledTableBody {...restProps} onSortEnd={handleReorder} useDragHandle />;
    },
    [handleReorder]
  );

  const Row = useMemo(
    () => ({ row, ...restProps }: any) => {
      return <StyledTableRow {...restProps} index={rows.indexOf(row)} />;
    },
    [rows]
  );

  const actionsNode = useMemo(() => actions && actions({ onReloadList: load }), [actions, load]);
  const rowActionsTooltipNode = useMemo(() => rowActionsTooltip && rowActionsTooltip(), [
    rowActionsTooltip,
  ]);

  const handleSetAsyncValues = useCallback(() => {
    setAsyncFilterValues(tempAsyncFilterValues);
    setCurrentPage(0);
    load({ page: 0, filters: tempAsyncFilterValues });
  }, [setAsyncFilterValues, tempAsyncFilterValues, load]);

  const handleClearFilters = useCallback(() => {
    setTempAsyncFilterValues({});
    setAsyncFilterValues({});
    setSyncFilterValues({});
    load({ page: 0, filters: {} });
  }, [setAsyncFilterValues, setTempAsyncFilterValues, load]);

  const handleFocusChange = useCallback(inFocus => {
    setDatePickerInFocus(inFocus);
  }, []);

  const handleCurrentPageChange = useCallback(
    page => {
      setCurrentPage(page);
      load({ page });
    },
    [setCurrentPage, load]
  );

  const handlePageSizeChange = useCallback(
    size => {
      setPageSize(size);
      setCurrentPage(0);
      load({ page: 0, pageSize: size });
    },
    [setPageSize, load]
  );

  const handleToggleClick = useCallback(
    event => {
      setToggleClosed(!toggleClosed);
    },
    [toggleClosed]
  );

  const toggleButtonComponent = useCallback(
    props => (
      <ColumnsMessage onClick={props.onToggle} ref={props.buttonRef}>
        <FormattedMessage {...messages.columns} />
        <Chevron toggleClosed={true} />
      </ColumnsMessage>
    ),
    [messages]
  );

  const customRowsMemo = useMemo(() => {
    if (typeof customRow === "function" && rows) {
      return customRow(rows);
    }
  }, [customRow, rows]);

  const filterLabels = useMemo(
    () =>
      messages?.tableHeaders &&
      Object.keys(messages?.tableHeaders).reduce((accum, curr) => {
        const obj = messages?.tableHeaders
          ? { [curr]: intl.formatMessage(messages?.tableHeaders[curr]) }
          : {};
        return { ...accum, ...obj };
      }, {}),
    [messages, intl]
  );

  const handleChangePills = useCallback(
    (filterValues?: Partial<F>) => {
      setAsyncFilterValues(filterValues || {});
      load({ filters: filterValues });
    },
    [load]
  );

  const pills = useMemo(
    () =>
      //@ts-ignore
      filterPills(asyncFilterValues, filterLabels, handleChangePills, setTempAsyncFilterValues),
    [asyncFilterValues, filterLabels, handleChangePills, setTempAsyncFilterValues]
  );

  const CSVReadyData = useMemo(() => {
    let columnsKeys = Object.keys(columns) || [];
    Object.values(columns).forEach(it => {
      if (typeof it.children === "object") {
        const childrenKeys = Object.keys(it.children);
        columnsKeys.push(...childrenKeys);
      }
    });
    const orderedFilteredKeys = columnsKeys.filter(it => !hiddenColumnNames.includes(it));

    return data
      ? data.map(entry =>
          orderedFilteredKeys.reduce<{}>((obj: { [key: string]: any }, key: keyof T & string) => {
            if (messages?.tableHeaders && messages?.tableHeaders[key]) {
              if (key === "translationsList") {
                const shortLocale = locale?.currentLocale?.substring(0, 2);
                const currentTranslations =
                  entry[key].find((it: any) => it.locale === shortLocale) || entry[key][0];

                Object.entries(currentTranslations).forEach(ent => {
                  if (messages?.tableHeaders && messages?.tableHeaders[ent[0]]) {
                    obj[intl.formatMessage(messages?.tableHeaders[ent[0]])] = ent[1];
                  }
                });
              } else if (
                typeof CSVRenderers === "object" &&
                Object.keys(CSVRenderers).includes(key) &&
                typeof CSVRenderers[key] === "function"
              ) {
                obj[intl.formatMessage(messages?.tableHeaders[key])] = CSVRenderers[key](
                  entry[key],
                  entry
                );
              } else {
                obj[intl.formatMessage(messages?.tableHeaders[key])] = entry[key];
              }
            }
            return obj;
          }, {})
        )
      : [];
  }, [data, hiddenColumnNames, messages, intl, columns, CSVRenderers, locale]);

  const hasAsyncFilters = useMemo(() => {
    if (filters) {
      return Object.values(filters).some((it: any) => it.filterFn === "async");
    }
  }, [filters]);

  const handleExport = useCallback(async () => {
    if (!onExport) {
      return;
    }
    let filterValues = filters ? asyncFilterValues : undefined;
    exportLoaderDispatch({ type: "load_start" });
    try {
      await onExport({ filterValues, currentPage, pageSize });
      exportLoaderDispatch({ type: "load_success", data: [] });
    } catch (e) {
      exportLoaderDispatch({
        type: "load_error",
        errorMessageId: "serviceError",
      });
      alert(intl.formatMessage(messages?.loadError));
      console.debug(e);
    }
  }, [
    onExport,
    filters,
    asyncFilterValues,
    exportLoaderDispatch,
    currentPage,
    pageSize,
    intl,
    messages,
  ]);

  // TODO: uncomment when back-end is implemented
  const showBulkUpdate = useMemo(
    () => false,
    []
    // onBulkUpdate && canBulkUpdate && authorizer.canExecute(canBulkUpdate),
    // [canBulkUpdate, onBulkUpdate]
  );

  const addBottomMarginToColumnOverlay = useMemo(
    () => (columns ? Object.keys(columns).length > 20 : false),
    [columns]
  );

  return (
    <CrudListContainer
      odd={rows.length % 2 === 0}
      addBottomMarginToColumnOverlay={addBottomMarginToColumnOverlay}
      resultsMsg={intl.formatMessage(messages?.resultPerPage)}>
      <SectionHeading>
        <FormattedMessage {...messages.entityName} />
      </SectionHeading>
      {subHeader && <SectionSubHeading>{subHeader}</SectionSubHeading>}
      {showSuccessMessage && (
        <div role="alert" className="alert alert-success">
          <strong>
            <FormattedMessage {...messages.saveSuccess} />
          </strong>
        </div>
      )}
      {filters && (
        <Accordion defaultActiveKey={filtersClosedByDefault ? undefined : "0"}>
          <Card>
            <Card.Body>
              <Accordion.Toggle
                as={CustomCardTitle}
                variant="link"
                eventKey="0"
                onClick={handleToggleClick}>
                <TitleChevronContainer>
                  <span>
                    <FormattedMessage {...messages.filtersTitle} />
                  </span>
                  <Chevron toggleClosed={toggleClosed} />
                  {toggleClosed && pills}
                </TitleChevronContainer>
              </Accordion.Toggle>
              <Accordion.Collapse eventKey="0" className="!visible">
                <>
                  <StyledCardDeck>
                    {Object.keys(filters).map(k => {
                      const key = k as keyof F & keyof T & string;
                      const filter = filters[key] as TableFilter<any, any>;
                      const Widget: React.ComponentType<FilterWidgetProps<unknown>> = filter.widget;
                      const async = filter.filterFn === "async";
                      const updater = async ? setTempAsyncFilterValues : setSyncFilterValues;
                      return (
                        <StyledSimpleRow key={key}>
                          <StyledCard>
                            <Widget
                              value={
                                async ? tempAsyncFilterValues[key] || "" : syncFilterValues[key]
                              }
                              onChange={newValue =>
                                updater(prev => ({
                                  ...prev,
                                  [key]: newValue,
                                }))
                              }
                              placeholder={
                                (filter.title && intl.formatMessage(filter.title)) ||
                                tableColumns.find(n => n.name === key)?.title ||
                                ""
                              }
                              onFocusChange={handleFocusChange}
                              focused={datePickerInFocus}
                              width={filter.width}
                            />
                          </StyledCard>
                        </StyledSimpleRow>
                      );
                    })}
                  </StyledCardDeck>
                  {hasAsyncFilters && (
                    <>
                      <Button variant="primary" onClick={handleSetAsyncValues}>
                        <FormattedMessage {...messages.filter} />
                      </Button>{" "}
                    </>
                  )}
                  <Button variant="primary" onClick={handleClearFilters}>
                    <FormattedMessage {...messages.clearFilters} />
                  </Button>
                </>
              </Accordion.Collapse>
            </Card.Body>
          </Card>
        </Accordion>
      )}

      <div style={{ marginBottom: 20 }} />
      <div
        className="ml-3"
        style={{
          marginBottom: "15px",
          display: "flex",
          justifyContent: "space-between",
        }}>
        <span style={{ display: "flex" }}>
          <div>{actionsNode}</div>
          {/*TODO by default it shouldn't show the export button without the need for hideExportButton*/}
          {!hideExportButton &&
            !nestedData &&
            (onExport ? (
              <ExportButton disabled={exportLoading} onClick={handleExport} variant="primary">
                {exportLoading ? (
                  <FormattedMessage {...messages?.loading} />
                ) : onExportText ? (
                  onExportText
                ) : (
                  <FormattedMessage {...messages.export} />
                )}
              </ExportButton>
            ) : (
              <CSVLink
                style={{ marginLeft: "10px", marginRight: "18px" }}
                data={CSVReadyData}
                filename={`${managerName}_export_${format(new Date(), "yyyyMMdd")}.csv`}>
                <Button variant="primary">
                  <FormattedMessage {...messages.export} />
                </Button>
              </CSVLink>
            ))}

          {showBulkUpdate && (
            <ExportButton onClick={onBulkUpdate} variant="primary">
              <FormattedMessage {...messages.bulkUpdate} />
            </ExportButton>
          )}

          {showBatchCancellation && (
            <ExportButton disabled={exportLoading} onClick={onBatchCancellation} variant="primary">
              <FormattedMessage {...messages.batchCancellation} />
            </ExportButton>
          )}

          {reordering && (
            <div style={{ marginLeft: "auto", alignSelf: "center" }}>
              <FontAwesomeIcon icon={faCircleNotch} spin />
            </div>
          )}
        </span>
        {additionalHeaderContent && (
          <div style={{ marginLeft: "20px", marginRight: "20px", overflow: "hidden" }}>
            {additionalHeaderContent}
          </div>
        )}
      </div>

      <SizeMe monitorHeight monitorWidth={false} noPlaceholder>
        {({ size }) => (
          <div style={{ flexGrow: 1 }} className="ml-3 mr-3">
            <Grid rows={rows} getRowId={getRowId} columns={tableColumns}>
              {paging && (
                <PagingState
                  currentPage={currentPage}
                  pageSize={pageSize}
                  onCurrentPageChange={handleCurrentPageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
              {paging && <CustomPaging totalCount={totalRowsCount} />}
              {uncontrolledPaging && (
                <PagingState
                  defaultCurrentPage={0}
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
              {uncontrolledPaging && <IntegratedPaging />}
              <VirtualTable
                columnExtensions={columnExtensions}
                height={listHeight}
                noDataCellComponent={NoDataCell}
                cellComponent={CellRenderer}
                bodyComponent={Body}
                rowComponent={customRowsMemo || Row}
                estimatedRowHeight={53}
              />
              <TableHeaderRow
                // if it is not done here, the tooltips do not work
                contentComponent={({ column, children, ...restProps }: any) => (
                  <TableHeaderRow.Content
                    {...restProps}
                    id={`column-id-${column.name}`}
                    data-tip="React-tooltip"
                    data-for={`tooltip-${column.name}`}>
                    {messages?.tableHeaders && messages?.tableHeaders[column.name] && (
                      <StyledReactTooltip id={`tooltip-${column.name}`} effect="solid">
                        <FormattedMessage
                          {...(messages?.tooltipHeaders && messages?.tooltipHeaders[column.name]
                            ? messages?.tooltipHeaders[column.name]
                            : messages?.tableHeaders[column.name])}
                        />
                      </StyledReactTooltip>
                    )}
                    {column.name === "__actions" && rowActionsTooltipNode}
                    {children}
                  </TableHeaderRow.Content>
                )}
              />
              {fixedActions && <TableFixedColumns rightColumns={["__actions"]} />}
              {columnVisibility && (
                <TableColumnVisibility
                  hiddenColumnNames={hiddenColumnNames}
                  onHiddenColumnNamesChange={setHiddenColumnNames}
                  columnExtensions={[
                    { columnName: "__actions", togglingEnabled: false },
                    ...columnVisibilityExtensions,
                  ]}
                />
              )}
              {columnVisibility && <Toolbar />}
              {columnVisibility && <ColumnChooser toggleButtonComponent={toggleButtonComponent} />}

              {(paging || uncontrolledPaging) && <PagingPanel pageSizes={pageSizes} />}
            </Grid>
            {additionalFooterContent}
          </div>
        )}
      </SizeMe>
    </CrudListContainer>
  );
}
